import {Box} from '@mui/material';
import {SxProps, Theme} from '@mui/material';
import {FC, useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {tariffsApi} from 'src/api';
import {useAppSelector} from 'src/app/hooks';
import {store} from 'src/app/store';
import {ClientListInTariffsInfo} from 'src/components/tariffs/ClientListInTariffsInfo';
import {TariffDescription} from 'src/components/tariffs/TariffDescription';
import {TariffIdentityCard} from 'src/components/tariffs/TariffIdentityCard';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {TariffByIdRequest} from 'src/types';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {useGetUserLanguage} from '../../hooks/useGetUserLanguage';

const pageSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    // Clears the app bar, as the list screens do now that their page header is gone.
    mt: 2.5,
};

type TariffByIdQueryTrigger = ReturnType<typeof tariffsApi.useLazyGetTariffByIdQuery>[0];

const handleGetTariffInfo =
    (trigger: TariffByIdQueryTrigger) => async (data: TariffByIdRequest) => {
        store.dispatch(preloaderChangeStatus(true));

        try {
            return await trigger(data).unwrap();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            store.dispatch(preloaderChangeStatus(false));
        }
    };

export const TariffInfo: FC = () => {
    const params = useParams();
    const {authInfo, activeBusiness} = useAppSelector((state) => state.common);
    const [trigger, response] = tariffsApi.useLazyGetTariffByIdQuery();

    // Each detail page asks about its own entity; `singlePages` was one subject for all of
    // them and, being absent from the catalog, sent every one of them straight to /404.
    const [currentLanguage] = useGetUserLanguage();

    useEffect(() => {
        Object.keys(authInfo).length &&
        params.id &&
        handleGetTariffInfo(trigger)({
            tariffId: +params.id,
            businessId: activeBusiness,
        });
    }, []);

    return (
        <>
            {response.data && (
                // One column of full-width sections. The plan has four facts, and beside a
                // description three times their height they left most of a column empty.
                <Box sx={pageSx}>
                    <TariffIdentityCard
                        data={response.data}
                        language={currentLanguage}
                    />
                    <TariffDescription data={response.data}/>
                    <ClientListInTariffsInfo
                        tariffId={response.data.id}
                        businessId={response.data.business.id}
                    />
                </Box>
            )}
        </>
    );
};
